<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { Route } from 'svelte-routing';
  import { api } from './lib/api';
  import { authStore } from './lib/auth';
  import { ApiError } from './lib/api/errorHandler';
  import { isPublicPath } from './lib/routeGuard';
  import { userSettings } from './lib/stores/settingsStore';
  import { categoriesStore } from './lib/stores/categoriesStore';
  import { householdsStore } from './lib/stores/householdsStore';
  import { followupsStore } from './lib/stores/followupsStore';
  import AppShell from './lib/components/layout/AppShell.svelte';
  import { Button } from './lib/components/ui/index';

  // Import routes
  import Home from './routes/Home.svelte';
  import Browse from './routes/Browse.svelte';
  import Inbox from './routes/Inbox.svelte';
  import Upload from './routes/Upload.svelte';
  import DocumentDetail from './routes/DocumentDetail.svelte';
  import Settings from './routes/Settings.svelte';
  import Login from './routes/Login.svelte';
  import AuthCallback from './routes/AuthCallback.svelte';
  import ReportDashboard from './routes/ReportDashboard.svelte';
  import ReportView from './routes/ReportView.svelte';
  import SettingsUsage from './routes/SettingsUsage.svelte';
  import NotFound from './routes/NotFound.svelte';
  import PrivacyPolicy from './routes/PrivacyPolicy.svelte';
  import ServerError from './routes/ServerError.svelte';
  import Duplicates from './routes/Duplicates.svelte';
  import Redirect from './lib/components/ui/Redirect.svelte';
  import Bin from './routes/Bin.svelte';
  import Followups from './routes/Followups.svelte';
  import SharedDocument from './routes/SharedDocument.svelte';

  // Spec 097 E2: dev server or runtime flag; read when the route renders, not at module load.
  const isStateCatalogEnabled = () =>
    import.meta.env.DEV || window.__APP_CONFIG__?.enableStateCatalog === 'true';

  let globalError: string | null = null;
  let autoDismissTimer: ReturnType<typeof setTimeout> | null = null;
  const AUTO_DISMISS_MS = 30_000;

  // Spec 015 E5: a logged-in but non-whitelisted user must see a dedicated
  // "account not provisioned" page, not the generic ServerError boundary.
  let accessDenied = false;

  // Spec 027 E4: a logged-in but non-provisioned user must stay strictly scoped to the
  // /share/:token view — the protected stores (whose load can 403 and trigger accessDenied)
  // must never be requested for that route in the first place. Reads window.location.pathname
  // directly (not svelte-routing's useLocation() store) — this is a synchronous mount/reactive-
  // trigger check, not a component reacting to in-app navigation, and avoids a hard dependency
  // on svelte-routing's Router context that App.svelte doesn't otherwise require.
  function isOnPublicPath(): boolean {
    return isPublicPath(window.location.pathname);
  }

  /**
   * Loads the initial protected stores. A 403 (EmailWhitelist) on any of them means
   * the account isn't provisioned — show the access-denied page instead of letting
   * the error fall through to the generic global error boundary (Spec 015 E5).
   *
   * Guards its own re-entry (already denied / already loaded) so callers can invoke
   * it unconditionally from a reactive statement without an eslint infinite-loop
   * false positive (the loop analysis can't see that `accessDenied` is checked here).
   *
   * Skips entirely on public paths (Spec 027 E4) — this check runs before any store.load()
   * call, so a non-provisioned user on /share/:token never triggers the 403 → accessDenied path.
   */
  async function loadInitialStores(): Promise<void> {
    if (isOnPublicPath()) return;
    if (accessDenied) return;
    if (
      userSettings.isLoaded &&
      categoriesStore.isLoaded &&
      householdsStore.isLoaded &&
      followupsStore.isLoaded
    )
      return;

    try {
      await Promise.all([
        userSettings.isLoaded ? Promise.resolve() : userSettings.load(),
        categoriesStore.isLoaded ? Promise.resolve() : categoriesStore.load(),
        householdsStore.isLoaded ? Promise.resolve() : householdsStore.load(),
        // Spec 086/F1: a failing followups load must not take down the whole app (no 403/
        // access-denied semantics apply here either) — the Topbar badge just stays empty and
        // `isLoaded` stays false, so the next auth-state trigger retries it.
        followupsStore.isLoaded ? Promise.resolve() : followupsStore.load().catch(() => {}),
      ]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        accessDenied = true;
      } else {
        throw err;
      }
    }
  }

  function scheduleAutoDismiss() {
    if (autoDismissTimer) clearTimeout(autoDismissTimer);
    autoDismissTimer = setTimeout(clearError, AUTO_DISMISS_MS);
  }

  function handleGlobalError(event: ErrorEvent) {
    globalError = event.message || 'Unknown error';
    scheduleAutoDismiss();
    event.preventDefault();
  }

  function handleUnhandledRejection(event: PromiseRejectionEvent) {
    globalError = event.reason?.message || String(event.reason) || 'Unknown error';
    scheduleAutoDismiss();
    event.preventDefault();
  }

  function clearError() {
    globalError = null;
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer);
      autoDismissTimer = null;
    }
  }

  onMount(() => {
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    api.settings.getHealth().catch(() => {});
    // Initial store load is handled by the `$: if ($authStore)` reactive statement
    // below, which also fires on this first run.
  });

  onDestroy(() => {
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    if (autoDismissTimer) clearTimeout(autoDismissTimer);
  });

  // Load stores when auth state changes (e.g. after login). The accessDenied/isLoaded
  // guards live inside loadInitialStores() itself so this reactive statement stays
  // clear of eslint's svelte/infinite-reactive-loop check.
  $: if ($authStore) {
    loadInitialStores();
  }

  function handleAccessDeniedLogout() {
    accessDenied = false;
    authStore.logout();
  }
</script>

<svelte:boundary>
  {#if accessDenied && !isOnPublicPath()}
    <!-- Spec 015 E5: non-whitelisted account — dedicated page, not ServerError.
         Spec 027 E4/S1: /share/:token is hoisted above this branch — a stale accessDenied
         flag (set on a previous route) must never swallow the scoped share view. -->
    <AppShell>
      <div
        class="flex flex-col items-center justify-center py-20 px-4 text-center"
        data-testid="access-denied"
      >
        <h1 class="text-2xl font-semibold text-text mb-2">{$_('pages.access_denied.title')}</h1>
        <p class="text-text-secondary mb-6 max-w-md mx-auto">
          {$_('pages.access_denied.message')}
        </p>
        <Button variant="secondary" on:click={handleAccessDeniedLogout}>
          {$_('pages.access_denied.logout_button')}
        </Button>
      </div>
    </AppShell>
  {:else if globalError}
    <!-- Global error boundary: catches unhandled errors -->
    <AppShell>
      <ServerError errorMessage={globalError} />
      <div class="flex justify-center mt-4">
        <button
          class="text-sm text-accent hover:text-accent-hover underline underline-offset-2"
          on:click={clearError}
        >
          {$_('pages.error.dismiss')}
        </button>
      </div>
    </AppShell>
  {:else}
    <!-- Auth callback: always available (OAuth redirect from Google).
         NOTE: slot syntax (<Route>…</Route>), not component={X}. svelte-routing
         v2 decides "is this a component or a lazy () => import() factory" via
         `component.toString().startsWith("class ")` — true for Svelte 4 class
         components, but Svelte 5 components are plain functions, so it wrongly
         treats them as factories and CALLS them with no anchor → first-paint
         crash ("Symbol(hmr anchor)" in dev, "reading 'before'" in prod). The
         slot form renders the child directly and sidesteps that path. -->
    <Route path="auth/callback"><AuthCallback /></Route>

    {#if $authStore}
      <!-- Logged in: /login redirects to / (handled by Login.svelte) -->
      <Route path="login"><Login /></Route>

      <!-- App shell with all protected routes -->
      <AppShell>
        <!-- Public share route also available when logged in (Spec 014 E9, S15) -->
        <Route path="share/:token" let:params>
          <SharedDocument token={params.token} />
        </Route>
        <!-- Spec 097: state catalog, lazy chunk, only behind the E2 gate (evaluated at render). -->
        <Route path="dev/states">
          {#if isStateCatalogEnabled()}
            {#await import('./routes/dev/StateCatalog.svelte') then catalog}
              <catalog.default />
            {/await}
          {:else}
            <NotFound />
          {/if}
        </Route>
        <Route path="inbox"><Inbox /></Route>
        <Route path="browse"><Browse /></Route>
        <Route path="upload/:batchId" let:params><Upload batchId={params.batchId} /></Route>
        <Route path="upload"><Upload /></Route>
        <Route path="documents/:id" let:params><DocumentDetail id={params.id} /></Route>
        <Route path="reports/:reportType" let:params
          ><ReportView reportType={params.reportType} /></Route
        >
        <Route path="reports"><ReportDashboard /></Route>
        <Route path="duplicates"><Duplicates /></Route>
        <Route path="tags"><Redirect to="/browse" /></Route>
        <Route path="bin"><Bin /></Route>
        <Route path="followups"><Followups /></Route>
        <Route path="settings/usage"><SettingsUsage /></Route>
        <Route path="settings"><Settings /></Route>
        <Route path="datenschutz"><PrivacyPolicy /></Route>
        <Route path="/"><Home /></Route>
        <Route default><NotFound /></Route>
      </AppShell>
    {:else}
      <!-- Not logged in: public share routes bypass auth guard (Spec 014 E9, S15).
           Route-based matching (not a bare <Login/>) so it only mounts when no
           other route matched. -->
      <Route path="share/:token" let:params>
        <SharedDocument token={params.token} />
      </Route>
      <Route default><Login /></Route>
    {/if}
  {/if}

  <!-- Safety net for unexpected render errors: show a fallback with a manual
       retry (reset), NOT an auto-retry — a blind reset()-on-error loops forever
       when the error is deterministic. -->
  {#snippet failed(error, reset)}
    <AppShell>
      <ServerError errorMessage={error instanceof Error ? error.message : String(error)} />
      <div class="flex justify-center mt-4">
        <button
          class="text-sm text-accent hover:text-accent-hover underline underline-offset-2"
          on:click={reset}
        >
          {$_('pages.error.retry')}
        </button>
      </div>
    </AppShell>
  {/snippet}
</svelte:boundary>
